import {
  type BizcycleProject,
  BusinessSteps,
  type BusinessStep,
  compareText,
  COMPETENCE_STEP,
  type IssueJobState,
  type JiraIssue,
  OUTER_STEPS,
  PRODUCTION_STEPS,
  routes,
  stepGroup,
} from '@pmai/contracts';
import {
  Alert,
  Button,
  CycleWheel,
  PageHeader,
  SelectField,
  Spinner,
  WHEEL_COLORS,
  type WheelNode,
} from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../shared/api/hooks';
import { useBackgroundJob } from '../../shared/api/jobs';
import { Can, usePermissions } from '../../shared/auth/session';
import { useEnumLabel, useErrorText } from '../../shared/i18n';
import {
  type BizFilters,
  inScope,
  isFiltered,
  matches,
  NO_FILTERS,
  scopedProjects,
  stepCounts,
  competences,
} from './bizcycle-model';
import { PendingApprovals } from './pending-approvals';
import { StepPanel } from './step-panel';

/** Biznesa aplis (FR-BIZ-001…017). */
export function BizcyclePage() {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const errorText = useErrorText();
  const canEdit = usePermissions().includes('bizcycle.edit');
  const issues = useApiQuery(routes.bizcycle.issues);
  const projects = useApiQuery(routes.bizcycle.projects);
  const technologies = useApiQuery(routes.technologies.list);
  const sync = useBackgroundJob(routes.bizcycle.sync, routes.bizcycle.syncState, {
    invalidates: [routes.bizcycle.issues],
  });
  const [filters, setFilters] = useState<BizFilters>(NO_FILTERS);
  const [selected, setSelected] = useState<BusinessStep | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  if (!issues.data || !projects.data) return <Spinner label={t('common.loading')} />;
  const all = issues.data;
  const unassigned = t('bizcycle.unassigned');
  const assigneeOf = (i: JiraIssue) => i.assigneeName ?? unassigned;
  const approved = all.filter((i) => i.approved);
  // Assignee and status choices come from the approved tasks of the chosen client/project; a
  // choice that is no longer among them no longer filters (FR-BIZ-012).
  const scope = approved.filter((i) => inScope(i, filters));
  const assignees = distinct(scope.map(assigneeOf));
  const statuses = distinct(scope.map((i) => i.statusName).filter(Boolean));
  const effective: BizFilters = {
    ...filters,
    assignee: assignees.includes(filters.assignee) ? filters.assignee : '',
    status: statuses.includes(filters.status) ? filters.status : '',
  };
  const shown = approved.filter((i) => matches(i, effective, assigneeOf));
  const counts = stepCounts(shown);
  const techName = (id: string) => technologies.data?.find((x) => x.id === id)?.name ?? '';
  const competenceList = competences(scopedProjects(projects.data, effective), techName);
  const dim = isFiltered(effective);

  const node = (step: BusinessStep): WheelNode => {
    const c = step === COMPETENCE_STEP ? { count: competenceList.length, burning: 0 } : counts.get(step);
    return {
      id: step,
      label: label(BusinessSteps.byCode(step)),
      group: stepGroup(step),
      count: c?.count ?? 0,
      burning: c?.burning ?? 0,
      dimmed: dim && !c?.count,
    };
  };

  const synced = (state: IssueJobState) =>
    setNotice(
      state.lastError || !state.lastResult
        ? { tone: 'danger', text: state.lastError ?? t('errors.INTERNAL') }
        : { tone: 'success', text: t('bizcycle.synced', state.lastResult) },
    );

  return (
    <>
      <PageHeader
        title={t('bizcycle.title')}
        actions={
          <Can perm="bizcycle.edit">
            <Button
              loading={sync.running}
              onClick={() => {
                setNotice(null);
                sync.run.mutate(
                  {},
                  { onSuccess: synced, onError: (e) => setNotice({ tone: 'danger', text: errorText(e) }) },
                );
              }}
            >
              {t('bizcycle.sync')}
            </Button>
          </Can>
        }
      />
      <div className="flex flex-col gap-5">
        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
        <Filters
          filters={effective}
          onChange={setFilters}
          projects={projects.data}
          assignees={assignees}
          statuses={statuses}
        />
        <div className="grid gap-5 xl:grid-cols-[minmax(0,720px)_1fr]">
          <div className="flex flex-col gap-3">
            <CycleWheel
              label={t('bizcycle.title')}
              outer={OUTER_STEPS.map(node)}
              inner={PRODUCTION_STEPS.map(node)}
              production="9"
              changeFrom="12"
              selected={selected}
              onSelect={(id) => setSelected((s) => (s === id ? null : (id as BusinessStep)))}
              centerTitle={t('bizcycle.center')}
              centerSubtitle={t('bizcycle.centerHint')}
              changeLabel={t('bizcycle.changeRequest')}
              nodeLabel={(n) => t('bizcycle.nodeLabel', { step: `${n.id} ${n.label}`, count: n.count })}
            />
            <ul aria-label={t('bizcycle.legend')} className="flex flex-wrap gap-4 text-sm text-ink-muted">
              {(['sales', 'deal', 'prod', 'care'] as const).map((g) => (
                <li key={g} className="flex items-center gap-1.5">
                  <span aria-hidden className="size-3 rounded-full" style={{ background: WHEEL_COLORS[g] }} />
                  {t(`bizcycle.groups.${g}`)}
                </li>
              ))}
            </ul>
          </div>
          {selected && (
            <StepPanel
              step={selected}
              issues={shown}
              projects={projects.data}
              competences={competenceList}
              technologies={technologies.data ?? []}
              canEdit={canEdit}
            />
          )}
        </div>
        <PendingApprovals
          issues={all.filter((i) => !i.approved && matches(i, effective, assigneeOf))}
          projects={projects.data}
          canEdit={canEdit}
        />
      </div>
    </>
  );
}

/**
 * Client → projects of that client (one project chosen by itself); a project sets its client;
 * assignees and statuses from the approved tasks in scope (FR-BIZ-012).
 */
const distinct = (values: string[]) => [...new Set(values)].sort(compareText);

function Filters({
  filters,
  onChange,
  projects,
  assignees,
  statuses,
}: {
  filters: BizFilters;
  onChange: (f: BizFilters) => void;
  projects: readonly BizcycleProject[];
  assignees: readonly string[];
  statuses: readonly string[];
}) {
  const { t } = useTranslation();
  const customers = [...new Map(projects.map((p) => [p.customerId, p.customerName]))]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => compareText(a.label, b.label));
  const ofCustomer = (id: string) => projects.filter((p) => !id || p.customerId === id);
  const options = (values: readonly string[]) => values.map((v) => ({ value: v, label: v }));
  return (
    <div
      role="search"
      aria-label={t('bizcycle.filters')}
      className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      <SelectField
        label={t('bizcycle.customer')}
        placeholder={t('bizcycle.allCustomers')}
        value={filters.customerId}
        options={customers}
        onChange={(e) => {
          const customerId = e.target.value;
          const own = ofCustomer(customerId);
          const projectId =
            customerId && own.length === 1
              ? own[0]!.id
              : own.some((p) => p.id === filters.projectId)
                ? filters.projectId
                : '';
          onChange({ ...filters, customerId, projectId });
        }}
      />
      <SelectField
        label={t('bizcycle.projectFilter')}
        placeholder={t('bizcycle.allProjects')}
        value={filters.projectId}
        options={ofCustomer(filters.customerId).map((p) => ({ value: p.id, label: p.name }))}
        onChange={(e) => {
          const projectId = e.target.value;
          const customerId = projects.find((p) => p.id === projectId)?.customerId ?? filters.customerId;
          onChange({ ...filters, projectId, customerId });
        }}
      />
      <SelectField
        label={t('bizcycle.assignee')}
        placeholder={t('bizcycle.all')}
        value={filters.assignee}
        options={options(assignees)}
        onChange={(e) => onChange({ ...filters, assignee: e.target.value })}
      />
      <SelectField
        label={t('bizcycle.status')}
        placeholder={t('bizcycle.all')}
        value={filters.status}
        options={options(statuses)}
        onChange={(e) => onChange({ ...filters, status: e.target.value })}
      />
    </div>
  );
}
